import { useState } from 'react'
import Seo from '@comp/seo'
import { motion } from 'framer-motion'
import BlogPost from '@interfaces/post'
import { getAllBlogPages } from '@lib/api'
import { blogCategories } from '../../blog.config'
import Layout from '@comp/layout'
import BlogCard from '@comp/blog/blog-card'
import Container from '@comp/ui/container'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

interface BlogIndexProps {
  locale: string
  posts: BlogPost[] | undefined
}

export default function BlogIndex({ locale, posts }: BlogIndexProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  const allPosts = posts ?? []
  const featured = allPosts.slice(0, 1)
  const rest = allPosts.slice(1)

  const filtered =
    selectedCategory === null
      ? rest
      : rest.filter((p) => p.category === selectedCategory)

  return (
    <Layout>
      <Seo
        title="Blog | Monofor"
        description="Articles, how-to's, and field notes on IAM, PAM, IGA, and modern identity from the Monofor team."
        path="/blog"
      />
      <main className="bg-surface text-ink-primary">
        <section className="relative overflow-hidden bg-surface pt-20 pb-16 lg:pt-28 lg:pb-20">
          <BackgroundField />
          <Container size="xl" className="relative">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <Eyebrow tone="brand">Field notes</Eyebrow>
            </motion.div>
            <motion.div
              className="mt-6 max-w-3xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.05 }}
            >
              <Heading as="h1" size="display-2xl">
                The Monofor blog.
              </Heading>
            </motion.div>
            <motion.div
              className="mt-6 max-w-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.12 }}
            >
              <Text size="xl" tone="secondary">
                Articles, how-to&apos;s, tutorials, and the occasional deep
                dive, written by the team that builds Monosign, Monopam, and
                Monosync.
              </Text>
            </motion.div>
          </Container>
        </section>

        {featured.length > 0 && (
          <section className="bg-surface pb-12 lg:pb-16">
            <Container size="xl">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, ease: EASE }}
              >
                <BlogCard
                  locale={locale}
                  post={featured[0]}
                  variant="featured"
                />
              </motion.div>
            </Container>
          </section>
        )}

        <section className="bg-surface pb-24 lg:pb-32">
          <Container size="xl">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <h2 className="text-display-md font-medium text-ink-primary">
                Latest articles
              </h2>
              <nav
                aria-label="Filter by category"
                className="flex flex-wrap items-center gap-2"
              >
                <CategoryPill
                  isActive={selectedCategory === null}
                  onClick={() => setSelectedCategory(null)}
                >
                  All
                </CategoryPill>
                {blogCategories.map((c) => (
                  <CategoryPill
                    key={c.slug}
                    isActive={selectedCategory === c.slug}
                    onClick={() => setSelectedCategory(c.slug)}
                  >
                    {c.title}
                  </CategoryPill>
                ))}
              </nav>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((post, i) => (
                <motion.div
                  key={post.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{
                    duration: 0.5,
                    ease: EASE,
                    delay: (i % 3) * 0.06,
                  }}
                >
                  <BlogCard locale={locale} post={post} />
                </motion.div>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="mt-12 rounded-3xl border border-line-subtle bg-surface-subtle p-12 text-center">
                <Text size="lg" tone="tertiary">
                  No posts in this category yet. Try another.
                </Text>
              </div>
            )}
          </Container>
        </section>
      </main>
    </Layout>
  )
}

function CategoryPill({
  isActive,
  onClick,
  children,
}: {
  isActive: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-pill px-4 py-2 text-body-sm font-medium transition duration-200 ease-smooth ${
        isActive
          ? 'bg-brand-900 text-white'
          : 'bg-surface-subtle text-ink-secondary hover:bg-surface-muted hover:text-ink-primary'
      }`}
    >
      {children}
    </button>
  )
}

function BackgroundField() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
      <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
      <svg
        className="absolute inset-0 h-full w-full text-brand-900/[0.04]"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <pattern
            id="grid-blog"
            width="56"
            height="56"
            patternUnits="userSpaceOnUse"
          >
            <path d="M.5 56V.5H56" fill="none" stroke="currentColor" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-blog)" />
      </svg>
    </div>
  )
}

export async function getStaticProps({ locale }: any) {
  const posts = getAllBlogPages(locale, [
    'slug',
    'title',
    'date',
    'coverImage',
    'summary',
    'image',
    'tags',
    'read_time',
    'published',
    'featured',
    'category',
  ])
  return {
    props: {
      locale,
      posts,
    },
  }
}
